/**
 * Workflow PDF Template
 * Generates workflow documentation with steps, notes, and audit log
 */

import {
  createPDF,
  addHeader,
  addFootersToAllPages,
  addSectionTitle,
  addKeyValue,
  addStatusBadge,
  checkPageBreak,
  generateFilename,
  downloadPDF,
  addTableOfContents,
  addEnhancedProgressBar,
  anonymizeName,
} from '../pdfGenerator';
import {
  colors,
  fontSizes,
  page,
  statusColors,
  priorityColors,
  hexToRgb,
  formatDate,
  formatDateTime,
  getStatusDisplay,
  getPriorityDisplay,
  documentTypes,
  tableStyles,
} from '../pdfStyles';

/**
 * Generate Workflow PDF
 * @param {Object} workflowData - Full workflow data with steps, notes, audit log
 * @param {Object} options - Export options
 * @returns {Promise<string>} - Filename
 */
export const generateWorkflowPDF = async (workflowData, options = {}) => {
  const { exportedBy = 'System', tenantName = 'Company', anonymize = false } = options;

  // Helper to conditionally anonymize names
  const getName = (name) => (anonymize ? anonymizeName(name) : name);

  const doc = createPDF('portrait');
  const headerOptions = {
    documentType: documentTypes.WORKFLOW,
    tenantName,
  };

  let y = addHeader(doc, headerOptions);

  // Document title
  doc.setFontSize(fontSizes.title);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...hexToRgb(colors.text));
  doc.text('WORKFLOW DOCUMENTATION', page.marginLeft, y + 5);
  y += 5;

  // Anonymization notice
  if (anonymize) {
    doc.setFontSize(fontSizes.small);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...hexToRgb(colors.warning));
    doc.text('(Names anonymized)', page.marginLeft, y + 5);
  }
  y += 10;

  // ===== TABLE OF CONTENTS =====
  const tocSections = [
    { title: 'Workflow Information', page: 1 },
    { title: 'Timeline', page: 1 },
    { title: 'Progress', page: 1 },
    { title: 'Workflow Steps', page: 2 },
    { title: 'Notes', page: 2 },
    { title: 'Audit Log', page: 3 },
  ];
  y = addTableOfContents(doc, tocSections, y, headerOptions);

  // Start new page after TOC
  doc.addPage();
  y = addHeader(doc, headerOptions);

  // ===== WORKFLOW INFORMATION =====
  y = addSectionTitle(doc, 'Workflow Information', y);

  y = addKeyValue(doc, 'Workflow ID', workflowData.workflow_id, y);
  y = addKeyValue(doc, 'Name', workflowData.name, y);
  y = addKeyValue(doc, 'Type', workflowData.workflow_type, y);
  y = addKeyValue(doc, 'Department', workflowData.department || 'N/A', y);
  y = addKeyValue(doc, 'Owner', getName(workflowData.owner) || 'N/A', y);

  // Status with badge
  doc.setFontSize(fontSizes.body);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...hexToRgb(colors.textLight));
  doc.text('Status:', page.marginLeft, y);
  addStatusBadge(doc, getStatusDisplay(workflowData.status), page.marginLeft + 40, y, statusColors);
  y += 5;

  // Priority with badge
  doc.setFontSize(fontSizes.body);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...hexToRgb(colors.textLight));
  doc.text('Priority:', page.marginLeft, y);
  addStatusBadge(doc, getPriorityDisplay(workflowData.priority), page.marginLeft + 40, y, priorityColors);
  y += 8;

  // ===== TIMELINE =====
  y = addSectionTitle(doc, 'Timeline', y);

  y = addKeyValue(doc, 'Start Date', formatDate(workflowData.start_date), y);
  y = addKeyValue(doc, 'Est. End Date', formatDate(workflowData.estimated_end_date) || 'Not Set', y);
  if (workflowData.actual_end_date) {
    y = addKeyValue(doc, 'Actual End', formatDate(workflowData.actual_end_date), y);
  }

  y += 5;

  // ===== PROGRESS =====
  y = addSectionTitle(doc, 'Progress', y);

  const steps = workflowData.workflow_steps || [];
  const completedSteps = steps.filter((s) => s.status === 'completed').length;
  const activeSteps = steps.filter((s) => s.status === 'active').length;
  const totalSteps = steps.length;

  // Enhanced progress bar
  y = addEnhancedProgressBar(doc, 'Steps', completedSteps, totalSteps, y);
  
  // Additional stats
  y = addKeyValue(doc, 'Active Steps', activeSteps.toString(), y, {
    valueColor: activeSteps > 0 ? colors.primary : colors.text,
  });
  y = addKeyValue(doc, 'Pending Steps', (totalSteps - completedSteps - activeSteps).toString(), y);

  y += 8;

  // ===== WORKFLOW STEPS =====
  y = checkPageBreak(doc, y, 40, headerOptions);
  y = addSectionTitle(doc, 'Workflow Steps', y);

  if (steps.length === 0) {
    doc.setFontSize(fontSizes.body);
    doc.setTextColor(...hexToRgb(colors.textLight));
    doc.text('No steps defined.', page.marginLeft, y);
    y += 8;
  } else {
    // Sort steps by order
    const sortedSteps = [...steps].sort((a, b) => (a.step_order || 0) - (b.step_order || 0));

    sortedSteps.forEach((step, index) => {
      y = checkPageBreak(doc, y, 25, headerOptions);

      const stepNum = index + 1;
      const statusIcon = step.status === 'completed' ? '✓' : step.status === 'active' ? '▶' : '○';
      const statusLabel =
        step.status === 'completed' ? '' : step.status === 'active' ? ' [ACTIVE]' : ' [PENDING]';

      // Step header
      doc.setFontSize(fontSizes.body);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...hexToRgb(colors.text));
      doc.text(`${stepNum}. ${statusIcon} ${step.step_name}${statusLabel}`, page.marginLeft, y);
      y += 5;

      // Step details
      doc.setFontSize(fontSizes.small);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...hexToRgb(colors.textLight));

      // Status and Duration
      const statusText = `Status: ${getStatusDisplay(step.status)}`;
      const durationText = step.duration ? `  │  Duration: ${step.duration}` : '';
      doc.text(`${statusText}${durationText}`, page.marginLeft + 5, y);
      y += 4;

      // Assigned to
      if (step.assigned_to) {
        doc.text(`Assigned: ${getName(step.assigned_to)}`, page.marginLeft + 5, y);
        y += 4;
      }

      // Completion date
      if (step.completed_at) {
        doc.text(`Completed: ${formatDate(step.completed_at)}`, page.marginLeft + 5, y);
        y += 4;
      } else if (step.started_at) {
        doc.text(`Started: ${formatDate(step.started_at)}`, page.marginLeft + 5, y);
        y += 4;
      }

      // Notes
      if (step.notes) {
        doc.setTextColor(...hexToRgb(colors.secondary));
        const noteLines = doc.splitTextToSize(`Notes: ${step.notes}`, page.contentWidth - 10);
        noteLines.slice(0, 3).forEach((line) => {
          doc.text(line, page.marginLeft + 5, y);
          y += 3.5;
        });
      }

      y += 4;
    });
  }

  // ===== WORKFLOW NOTES =====
  y = checkPageBreak(doc, y, 30, headerOptions);
  y = addSectionTitle(doc, 'Notes', y);

  const notes = workflowData.workflow_notes || [];

  if (notes.length === 0) {
    doc.setFontSize(fontSizes.body);
    doc.setTextColor(...hexToRgb(colors.textLight));
    doc.text('No notes recorded.', page.marginLeft, y);
    y += 8;
  } else {
    // Sort notes by date (newest first)
    const sortedNotes = [...notes].sort((a, b) => {
      return new Date(b.created_at) - new Date(a.created_at);
    });

    sortedNotes.forEach((note) => {
      y = checkPageBreak(doc, y, 15, headerOptions);

      // Note header
      doc.setFontSize(fontSizes.small);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...hexToRgb(colors.secondary));
      doc.text(`${formatDate(note.actual_note_date || note.created_at)} - ${getName(note.created_by) || 'Unknown'}`, page.marginLeft, y);
      y += 4;

      // Note title
      if (note.note_title) {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...hexToRgb(colors.text));
        doc.text(note.note_title, page.marginLeft, y);
        y += 4;
      }

      // Note content
      if (note.note_content) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...hexToRgb(colors.text));
        const contentLines = doc.splitTextToSize(note.note_content, page.contentWidth);
        contentLines.slice(0, 4).forEach((line) => {
          doc.text(line, page.marginLeft, y);
          y += 3.5;
        });
      }

      y += 4;
    });
  }

  // ===== AUDIT LOG =====
  y = checkPageBreak(doc, y, 30, headerOptions);
  y = addSectionTitle(doc, 'Audit Log', y);

  const auditLog = workflowData.workflow_audit_log || [];

  if (auditLog.length === 0) {
    doc.setFontSize(fontSizes.body);
    doc.setTextColor(...hexToRgb(colors.textLight));
    doc.text('No audit entries.', page.marginLeft, y);
    y += 8;
  } else {
    // Sort by date (newest first) and limit to last 20
    const sortedAudit = [...auditLog]
      .sort((a, b) => new Date(b.performed_at || b.created_at) - new Date(a.performed_at || a.created_at))
      .slice(0, 20);

    const auditRows = sortedAudit.map((entry) => [
      formatDateTime(entry.performed_at || entry.created_at),
      entry.action || 'Action',
      entry.details || '',
      getName(entry.performed_by) || 'System',
    ]);

    doc.autoTable({
      startY: y,
      head: [['Date/Time', 'Action', 'Details', 'By']],
      body: auditRows,
      ...tableStyles.compact,
      columnStyles: {
        0: { cellWidth: 35 },
        1: { cellWidth: 35 },
        2: { cellWidth: 70 },
        3: { cellWidth: 40 },
      },
      margin: { left: page.marginLeft, right: page.marginRight },
      didDrawPage: (data) => {
        if (data.pageNumber > 1) {
          addHeader(doc, headerOptions);
        }
      },
    });

    y = doc.lastAutoTable.finalY + 8;
  }

  // ===== EXPORT METADATA =====
  y = checkPageBreak(doc, y, 20, headerOptions);
  doc.setDrawColor(...hexToRgb(colors.border));
  doc.setLineWidth(0.5);
  doc.line(page.marginLeft, y, page.contentWidth + page.marginLeft, y);
  y += 5;

  doc.setFontSize(fontSizes.small);
  doc.setTextColor(...hexToRgb(colors.textLight));
  doc.text(`Export Type: Workflow Documentation${anonymize ? ' - Anonymized' : ''}`, page.marginLeft, y);
  y += 4;
  doc.text(`Exported: ${formatDateTime(new Date())}`, page.marginLeft, y);
  y += 4;
  doc.text(`Exported By: ${exportedBy}`, page.marginLeft, y);

  // Add footers to all pages
  addFootersToAllPages(doc, {
    exportedBy,
    exportDate: new Date(),
  });

  // Generate filename and download
  const filename = generateFilename(workflowData.workflow_id, 'workflow');
  downloadPDF(doc, filename);

  return filename;
};

export default generateWorkflowPDF;
